import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import Pluralize from 'react-pluralize';
import OneWayFlightDetails from './OneWayFlightDetails';
import formatDuration from '../../../../helpers/formatters/formatDuration';
import flightSearchInput from '../../../../helpers/selectors/flightSearchInput';
import { FlightSearchTypes, SearchModes } from '../../../../constants';
import getHighestRankedCabinFlightItinerary from '../../../../helpers/flightCabins/getHighestRankedCabinFlightItinerary';
import getPreferredCabins from '../../../../helpers/flightCabins/getPreferredCabins';
import getPreferredCabinByFlightSegmentIndex from '../../../../helpers/flightCabins/getPreferredCabinByFlightSegmentIndex';
import { checkDoEnableSelectFlightsBySegment } from '../../../../store/slices/user';

const FlightSegmentDetail = ({
  segment,
  doShowPrice,
  label,
  preferredCabin,
  avoidCabinDowngrade,
  highestRankedCabin,
}) => (
  <div className="lg:mx-10 lg:text-base text-sm mx-0 my-2">
    <div className="grid grid-cols-2 text-theme border-2 p-2 mb-3">
      <p className="font-bold">
        <span className="font-smallerHeading text-smallerHeading">{label}</span>
      </p>
      <div className="text-right">
        {formatDuration(segment.durationInMin)},{' '}
        <Pluralize singular="Stop" count={segment.stops} zero="Nonstop" />
      </div>
    </div>
    <OneWayFlightDetails
      flights={segment.flights}
      doShowPrice={doShowPrice}
      preferredCabin={preferredCabin}
      avoidCabinDowngrade={avoidCabinDowngrade}
      highestRankedCabin={highestRankedCabin}
    />
  </div>
);

const FlightDetails = ({ itinerary, doShowPrice, tripId, selected, isConfirmation }) => {
  const { selectedSearchMode } = useSelector((store) => store.tripSearch);
  const { isViewingBrochure } = useSelector((state) => state.brochure);
  const { isViewingCompare } = useSelector((state) => state.compare);
  const isTripSearch = selectedSearchMode === SearchModes.FLIGHT;
  let doEnableSelectFlightsBySegment = useSelector(checkDoEnableSelectFlightsBySegment);
  // caveat here, don't use isTripSearch = !tripId because we always have tripId when view details.
  const { searchObj, breadCrumbsIndex } = useSelector(flightSearchInput(tripId, isTripSearch));
  const isDirectSell = searchObj.flightSearchType === FlightSearchTypes.DIRECT_SELL;

  if (
    isViewingBrochure ||
    isViewingCompare ||
    (!isTripSearch && selected) ||
    isConfirmation ||
    isDirectSell
  ) {
    doEnableSelectFlightsBySegment = false;
  }
  const flightSegmentIndexToShow =
    doEnableSelectFlightsBySegment && breadCrumbsIndex >= 0 ? breadCrumbsIndex : -1;

  const getLabel = useCallback(
    (index) => {
      if (itinerary.isMultiCity) {
        return `Flight ${index + 1}`;
      }
      if (index === 0) {
        return 'Outbound';
      }
      if (index === 1) {
        return 'Inbound';
      }
      return '';
    },
    [itinerary.isMultiCity],
  );

  const highestRankedCabin =
    !isViewingBrochure && !isViewingCompare ? getHighestRankedCabinFlightItinerary(itinerary) : '';
  const preferredCabins =
    !isViewingBrochure && !isViewingCompare ? getPreferredCabins(searchObj) : [];

  const getPreferredCabin = (index) =>
    getPreferredCabinByFlightSegmentIndex(preferredCabins, index);

  const { avoidCabinDowngrade } = searchObj;

  return itinerary.flightSegments.map((flightSegment, index) =>
    !doEnableSelectFlightsBySegment ||
    itinerary.flightSegments.length === 1 ||
    flightSegmentIndexToShow === index ? (
      <FlightSegmentDetail
        key={`${index.toString()}}`}
        segment={flightSegment}
        label={getLabel(index)}
        doShowPrice={doShowPrice}
        avoidCabinDowngrade={avoidCabinDowngrade}
        preferredCabin={getPreferredCabin(index)}
        highestRankedCabin={highestRankedCabin}
      />
    ) : null,
  );
};

FlightDetails.propTypes = {
  itinerary: PropTypes.objectOf(PropTypes.objectOf),
  doShowPrice: PropTypes.bool,
  tripId: PropTypes.string,
  selected: PropTypes.bool,
  isConfirmation: PropTypes.bool,
};

FlightDetails.defaultProps = {
  itinerary: {},
  doShowPrice: true,
  tripId: null,
  selected: false,
  isConfirmation: false,
};

FlightSegmentDetail.propTypes = {
  segment: PropTypes.objectOf(PropTypes.objectOf),
  doShowPrice: PropTypes.bool,
  label: PropTypes.string,
  preferredCabin: PropTypes.string,
  avoidCabinDowngrade: PropTypes.bool.isRequired,
  highestRankedCabin: PropTypes.string,
};

FlightSegmentDetail.defaultProps = {
  segment: {},
  doShowPrice: true,
  label: '',
  preferredCabin: null,
  highestRankedCabin: '',
};

export default React.memo(FlightDetails);
